<template>
  <div class="chat-panel" :class="{ resizing: isResizing }" :style="panelStyle">
    <!-- Resizable top border (VS Code terminal-style drag handle) -->
    <div
      class="chat-resize-handle"
      :class="{ active: isResizing }"
      @mousedown="startResize"
      @dblclick="resetHeight"
      title="Drag to resize · double-click to reset"
    ></div>
    <div class="chat-panel-header">
      <h3>Chats</h3>
      <div class="chat-panel-header-actions">
        <a-button
          type="link"
          size="small"
          @click="clearFilter"
          v-if="filteredLoadId"
        >
          All chats
        </a-button>
        <a-button
          type="link"
          size="small"
          icon="close"
          class="chat-panel-close"
          title="Close chat"
          @click="closePanel"
        />
      </div>
    </div>

    <div class="chat-panel-content">
      <!-- Chat List -->
      <div class="chat-list">
        <div
          v-for="chat in chats"
          :key="`${chat.load_id}-${chat.user_id}`"
          class="chat-item"
          :class="{ active: isActiveChat(chat) }"
          @click="selectChat(chat)"
        >
          <div class="chat-avatar">
            {{ getChatListInitials(chat) }}
          </div>
          <div class="chat-info">
            <div class="chat-name">{{ getChatListName(chat) }}</div>
            <div class="chat-preview" v-if="chat.last_message">
              {{ truncateMessage(chat.last_message.message) }}
            </div>
            <div class="chat-load-id">Load: {{ chat.external_load_id }}</div>
          </div>
          <div class="chat-meta">
            <div class="chat-time" v-if="chat.last_message">
              {{ formatTime(chat.last_message.created_at) }}
            </div>
            <a-badge
              v-if="chat.unread_count > 0"
              :count="chat.unread_count"
              class="chat-badge"
            />
          </div>
        </div>
        <div v-if="chats.length === 0" class="no-chats">
          No chats yet
        </div>
      </div>

      <!-- Chat Messages -->
      <div class="chat-messages-container" v-if="selectedChat">
        <div class="chat-messages-header">
          <div class="chat-partner-info">
            <span class="status-dot online"></span>
            <span class="partner-name">{{ chatPartnerName }}</span>
            <span class="status-text">(Active)</span>
          </div>
        </div>

        <div class="chat-messages" ref="messagesContainer">
          <div
            v-for="message in messages"
            :key="message.id"
            class="message"
            :class="{ 'own': isOwnMessage(message) }"
          >
            <div class="message-avatar" v-if="!isOwnMessage(message)">
              {{ getMessageSenderInitials(message) }}
            </div>
            <div class="message-content">
              <div class="message-text">{{ message.message }}</div>
              <div class="message-file" v-if="message.file_url">
                <a :href="message.file_url" target="_blank" class="file-link">
                  <a-icon type="paper-clip" />
                  {{ message.file_name || 'Attachment' }}
                </a>
              </div>
              <div class="message-time">
                {{ formatMessageTime(message.created_at) }}
                <a-icon v-if="isOwnMessage(message) && message.is_read" type="check" class="read-icon" />
              </div>
            </div>
          </div>
          <div v-if="messages.length === 0" class="no-messages">
            No messages yet. Start the conversation!
          </div>
        </div>

        <div class="chat-input">
          <a-input
            v-model="newMessage"
            placeholder="Type a message..."
            @pressEnter="sendMessage"
            :disabled="sending"
          />
          <a-button
            type="primary"
            icon="arrow-right"
            @click="sendMessage"
            :loading="sending"
            :disabled="!newMessage.trim()"
          />
        </div>
      </div>

      <div v-else class="no-chat-selected">
        <a-icon type="message" style="font-size: 48px; color: #ccc;" />
        <p>Select a chat to start messaging</p>
      </div>

      <!-- Load Info Sidebar -->
      <div class="load-info-sidebar" v-if="selectedChat && loadInfo">
        <div class="load-info-header">
          <span class="load-id">Load: {{ loadInfo.external_load_id }}</span>
        </div>
        <div class="load-info-details">
          <div class="load-info-row">
            <span class="label">Origin</span>
            <span class="value">{{ loadInfo.origin_city_name }}, {{ loadInfo.origin_state_code }}</span>
          </div>
          <div class="load-info-row">
            <span class="label">Destination</span>
            <span class="value">{{ loadInfo.destination_city_name }}, {{ loadInfo.destination_state_code }}</span>
          </div>
          <div class="load-info-row">
            <span class="label">Pickup Date</span>
            <span class="value">{{ formatPickupDate(loadInfo.scheduled_pickup_date_time) }}</span>
          </div>
          <div class="load-info-row">
            <span class="label">Total Distance</span>
            <span class="value">{{ loadInfo.total_distance }} miles</span>
          </div>
          <div class="load-info-row">
            <span class="label">Bidding Ends In</span>
            <span class="value highlight">{{ formatBiddingEnds(loadInfo.bidding_round_ends) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import chatAPI from '@/api/chat'
import loadAPI from '@/api/load'

export default {
  name: 'ChatPanel',
  props: {
    // Optional: filter chats by load ID
    loadId: {
      type: String,
      default: null
    },
    // Optional: pre-select a specific chat
    initialUserId: {
      type: String,
      default: null
    },
    // Height of the panel
    height: {
      type: String,
      default: 'calc(100vh - 200px)'
    }
  },
  data() {
    return {
      chats: [],
      messages: [],
      selectedChat: null,
      loadInfo: null,
      newMessage: '',
      sending: false,
      loading: false,
      filteredLoadId: null,
      userType: localStorage.getItem('userType') || 'user',
      currentUserId: null,
      // Resizable panel (VS Code terminal-style)
      panelHeight: 0,
      isResizing: false,
      minHeight: 160,
      // Horizontal docking — aligned to the content area (clears the sidebar)
      panelLeft: 0,
      panelRight: 0,
      contentObserver: null
    }
  },
  computed: {
    panelStyle() {
      return {
        height: this.panelHeight + 'px',
        left: this.panelLeft + 'px',
        right: this.panelRight + 'px'
      }
    },
    isAdmin() {
      return this.userType === 'admin'
    },
    chatPartnerName() {
      if (!this.selectedChat) return ''
      // For users, they're chatting with admin - show "GRM Admin"
      if (!this.isAdmin) {
        return 'GRM Admin'
      }
      // For admins, show the user's name
      return this.selectedChat.user_name || 'User'
    }
  },
  watch: {
    loadId: {
      immediate: true,
      handler(newVal) {
        this.filteredLoadId = newVal
        this.loadChats()
      }
    },
    initialUserId: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.chats.length > 0) {
          const chat = this.chats.find(c => c.user_id === newVal)
          if (chat) {
            this.selectChat(chat)
          }
        }
      }
    }
  },
  mounted() {
    this.initPanelHeight()
    window.addEventListener('resize', this.handleWindowResize)
    this.$nextTick(() => this.setupContentTracking())
    this.getCurrentUserId()
    this.loadChats()
    this.setupWebSocketListeners()
  },
  beforeDestroy() {
    this.removeWebSocketListeners()
    window.removeEventListener('resize', this.handleWindowResize)
    if (this.contentObserver) {
      this.contentObserver.disconnect()
      this.contentObserver = null
    }
    this.stopResize()
  },
  methods: {
    // --- Resizable panel (VS Code terminal-style) ---
    initPanelHeight() {
      const saved = parseInt(localStorage.getItem('chatPanelHeight'), 10)
      const fallback = Math.round(window.innerHeight * 0.4) // default: 40% of screen
      this.panelHeight = this.clampHeight(saved > 0 ? saved : fallback)
    },

    clampHeight(h) {
      return Math.max(this.minHeight, Math.min(h, window.innerHeight))
    },

    startResize(e) {
      e.preventDefault()
      this.isResizing = true
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'ns-resize'
      window.addEventListener('mousemove', this.onResize)
      window.addEventListener('mouseup', this.stopResize)
    },

    onResize(e) {
      if (!this.isResizing) return
      // Height = distance from the cursor to the bottom of the viewport
      this.panelHeight = this.clampHeight(window.innerHeight - e.clientY)
    },

    stopResize() {
      if (!this.isResizing) return
      this.isResizing = false
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      window.removeEventListener('mousemove', this.onResize)
      window.removeEventListener('mouseup', this.stopResize)
      localStorage.setItem('chatPanelHeight', String(this.panelHeight))
    },

    resetHeight() {
      this.panelHeight = this.clampHeight(Math.round(window.innerHeight * 0.4))
      localStorage.setItem('chatPanelHeight', String(this.panelHeight))
    },

    handleWindowResize() {
      this.panelHeight = this.clampHeight(this.panelHeight)
      this.updatePanelBounds()
    },

    // Align the dock horizontally to the page content area so it clears the
    // left sidebar (like VS Code's terminal sitting above the file explorer).
    setupContentTracking() {
      this.updatePanelBounds()
      const content = this.getContentEl()
      if (content && typeof ResizeObserver !== 'undefined') {
        // Re-align when the content area changes (e.g. sidebar minimize/expand)
        this.contentObserver = new ResizeObserver(() => this.updatePanelBounds())
        this.contentObserver.observe(content)
      }
    },

    getContentEl() {
      return document.querySelector('#layout-dashboard .ant-layout-content') ||
             document.querySelector('.layout-dashboard .ant-layout-content')
    },

    updatePanelBounds() {
      const content = this.getContentEl()
      if (content) {
        const rect = content.getBoundingClientRect()
        this.panelLeft = Math.max(0, Math.round(rect.left))
        this.panelRight = Math.max(0, Math.round(window.innerWidth - rect.right))
      } else {
        this.panelLeft = 0
        this.panelRight = 0
      }
    },

    getCurrentUserId() {
      try {
        const userData = localStorage.getItem('user')
        if (userData) {
          const user = JSON.parse(userData)
          this.currentUserId = user.id
          console.log('Chat: Current user ID:', this.currentUserId, 'User type:', this.userType)
        }
      } catch (e) {
        console.error('Error getting current user ID:', e)
      }
    },

    async loadChats() {
      this.loading = true
      try {
        const response = await chatAPI.getChats(this.filteredLoadId)
        this.chats = response.data.chats || []

        // Auto-select initial chat if specified
        if (this.initialUserId && this.filteredLoadId) {
          const chat = this.chats.find(
            c => c.user_id === this.initialUserId && c.load_id === this.filteredLoadId
          )
          if (chat) {
            this.selectChat(chat)
          }
        }
      } catch (error) {
        console.error('Error loading chats:', error)
        this.$message.error('Failed to load chats')
      } finally {
        this.loading = false
      }
    },

    async selectChat(chat) {
      this.selectedChat = chat
      await Promise.all([
        this.loadMessages(),
        this.loadLoadInfo(chat.load_id)
      ])
      await this.markAsRead()
      this.$emit('chat-selected', chat)
    },

    async loadLoadInfo(loadId) {
      try {
        const response = await loadAPI.getLoadById(loadId)
        this.loadInfo = response
      } catch (error) {
        console.error('Error loading load info:', error)
        this.loadInfo = null
      }
    },

    async loadMessages() {
      if (!this.selectedChat) return

      try {
        const response = await chatAPI.getMessages(
          this.selectedChat.load_id,
          this.selectedChat.user_id
        )
        this.messages = response.data.messages || []
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } catch (error) {
        console.error('Error loading messages:', error)
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() || !this.selectedChat || this.sending) return

      this.sending = true
      const messageText = this.newMessage.trim()
      try {
        const response = await chatAPI.sendMessage(
          this.selectedChat.load_id,
          this.selectedChat.user_id,
          messageText
        )
        this.newMessage = ''

        // Optimistic UI update - add message immediately
        if (response.data && response.data.message) {
          const newMsg = response.data.message
          // Avoid duplicates (in case WebSocket also delivers it)
          if (!this.messages.find(m => m.id === newMsg.id)) {
            this.messages.push(newMsg)
            this.$nextTick(() => {
              this.scrollToBottom()
            })
          }
        }
      } catch (error) {
        console.error('Error sending message:', error)
        this.$message.error('Failed to send message')
      } finally {
        this.sending = false
      }
    },

    async markAsRead() {
      if (!this.selectedChat) return

      try {
        await chatAPI.markAsRead(
          this.selectedChat.load_id,
          this.selectedChat.user_id
        )
        // Update local unread count
        const chat = this.chats.find(
          c => c.load_id === this.selectedChat.load_id &&
               c.user_id === this.selectedChat.user_id
        )
        if (chat) {
          chat.unread_count = 0
        }
      } catch (error) {
        console.error('Error marking as read:', error)
      }
    },

    clearFilter() {
      this.filteredLoadId = null
      this.loadChats()
      this.$emit('filter-cleared')
    },

    // Close the entire chat dock (parent hides the panel)
    closePanel() {
      this.$emit('close-panel')
    },

    filterByLoad(loadId) {
      this.filteredLoadId = loadId
      this.loadChats()
    },

    isActiveChat(chat) {
      return this.selectedChat &&
             this.selectedChat.load_id === chat.load_id &&
             this.selectedChat.user_id === chat.user_id
    },

    isOwnMessage(message) {
      // For admin users, check if sender_type is 'admin'
      if (this.isAdmin) {
        return message.sender_type === 'admin'
      }
      // For regular users, check if sender_id matches current user
      // Convert both to strings for comparison
      const senderId = String(message.sender_id || '')
      const currentId = String(this.currentUserId || '')
      return senderId === currentId
    },

    getInitials(name) {
      if (!name) return 'U'
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    },

    getMessageSenderInitials(message) {
      // For users viewing admin messages, show "GA" for GRM Admin
      if (!this.isAdmin && message.sender_type === 'admin') {
        return 'GA'
      }
      return this.getInitials(message.sender_name || 'U')
    },

    getChatListName(chat) {
      // For users, show "GRM Admin" since they're chatting with admin
      if (!this.isAdmin) {
        return 'GRM Admin'
      }
      // For admins, show the user's name
      return chat.user_name || 'Unknown User'
    },

    getChatListInitials(chat) {
      // For users, show "GA" for GRM Admin
      if (!this.isAdmin) {
        return 'GA'
      }
      // For admins, show user's initials
      return this.getInitials(chat.user_name || 'U')
    },

    truncateMessage(message) {
      if (!message) return ''
      return message.length > 30 ? message.substring(0, 30) + '...' : message
    },

    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now - date

      if (diff < 60000) return 'Just now'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
      if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      return date.toLocaleDateString()
    },

    formatMessageTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    formatPickupDate(timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp)
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    },

    formatBiddingEnds(timestamp) {
      if (!timestamp) return 'N/A'
      const end = new Date(timestamp)
      const now = new Date()
      const diff = end - now

      if (diff <= 0) return 'Ended'

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

      if (hours > 0) {
        return `${hours}h ${minutes}m`
      }
      return `${minutes}m`
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    setupWebSocketListeners() {
      if (this.$wsOn) {
        this.$wsOn('chat_message', this.handleNewMessage)
        this.$wsOn('chat_read', this.handleChatRead)
      }
    },

    removeWebSocketListeners() {
      if (this.$wsOff) {
        this.$wsOff('chat_message', this.handleNewMessage)
        this.$wsOff('chat_read', this.handleChatRead)
      }
    },

    handleNewMessage(data) {
      // The WebSocket service emits the full message object with type and payload
      const payload = data.payload || data
      const message = payload.message
      const loadId = payload.load_id
      const userId = payload.user_id

      console.log('Chat: Received new message via WebSocket:', data)

      // Add message if it's for the currently selected chat
      if (this.selectedChat &&
          this.selectedChat.load_id === loadId &&
          this.selectedChat.user_id === userId) {
        // Avoid duplicates
        if (!this.messages.find(m => m.id === message.id)) {
          this.messages.push(message)
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }

        // Mark as read since we're viewing this chat
        if (!this.isOwnMessage(message)) {
          this.markAsRead()
        }
      } else {
        // Update unread count for other chats
        const chat = this.chats.find(
          c => c.load_id === loadId && c.user_id === userId
        )
        if (chat && !this.isOwnMessage(message)) {
          chat.unread_count = (chat.unread_count || 0) + 1
          chat.last_message = message
        } else if (!chat) {
          // New chat, reload the list
          this.loadChats()
        }
      }
    },

    handleChatRead(payload) {
      // Update read status in messages if needed
      if (this.selectedChat &&
          this.selectedChat.load_id === payload.load_id &&
          this.selectedChat.user_id === payload.user_id) {
        // Messages have been read
        this.messages.forEach(m => {
          if (this.isOwnMessage(m)) {
            m.is_read = true
          }
        })
      }
    },

    // Public method to open a specific chat
    openChat(loadId, userId, userName = null, companyName = null) {
      const existingChat = this.chats.find(
        c => c.load_id === loadId && c.user_id === userId
      )

      if (existingChat) {
        this.selectChat(existingChat)
      } else {
        // Create a temporary chat object
        const tempChat = {
          load_id: loadId,
          user_id: userId,
          user_name: userName,
          company_name: companyName,
          external_load_id: loadId,
          unread_count: 0
        }
        this.selectChat(tempChat)
      }
    }
  }
}
</script>

<style scoped>
.chat-panel {
  display: flex;
  flex-direction: column;
  /* height + horizontal docking (left/right) are controlled inline via :style */
  border: 1px solid #e8e8e8;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: #fff;
  overflow: hidden;
  position: fixed;
  bottom: 0;
  z-index: 1000;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);
}

/* Disable transitions/selection while dragging for smooth resize */
.chat-panel.resizing {
  user-select: none;
}

/* Resizable top border — the drag handle */
.chat-resize-handle {
  height: 6px;
  flex-shrink: 0;
  cursor: ns-resize;
  background: transparent;
  position: relative;
  transition: background-color 0.15s;
}

.chat-resize-handle::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 2px;
  border-radius: 2px;
  background: #d9d9d9;
  transition: background-color 0.15s;
}

.chat-resize-handle:hover,
.chat-resize-handle.active {
  background: rgba(24, 144, 255, 0.12);
}

.chat-resize-handle:hover::before,
.chat-resize-handle.active::before {
  background: #1890ff;
}

.chat-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
  flex-shrink: 0;
}

.chat-panel-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.chat-panel-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.chat-panel-close {
  color: #8c8c8c;
}

.chat-panel-close:hover {
  color: #ff4d4f;
}

.chat-panel-content {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.chat-list {
  width: 280px;
  border-right: 1px solid #e8e8e8;
  overflow-y: auto;
  flex-shrink: 0;
  max-height: 100%;
}

.chat-list::-webkit-scrollbar {
  width: 6px;
}

.chat-list::-webkit-scrollbar-thumb {
  background-color: #d9d9d9;
  border-radius: 3px;
}

.chat-list::-webkit-scrollbar-track {
  background-color: #f5f5f5;
}

.chat-item {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.chat-item:hover {
  background-color: #f5f5f5;
}

.chat-item.active {
  background-color: #e6f7ff;
}

.chat-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #1890ff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

.chat-avatar.small {
  width: 32px;
  height: 32px;
  font-size: 12px;
}

.chat-info {
  flex: 1;
  margin-left: 12px;
  overflow: hidden;
}

.chat-name {
  font-weight: 500;
  color: #262626;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-preview {
  font-size: 12px;
  color: #8c8c8c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-load-id {
  font-size: 11px;
  color: #bfbfbf;
}

.chat-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 8px;
}

.chat-time {
  font-size: 11px;
  color: #8c8c8c;
}

.chat-badge {
  margin-top: 4px;
}

.no-chats {
  padding: 24px;
  text-align: center;
  color: #8c8c8c;
}

.chat-messages-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.chat-messages-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
  flex-shrink: 0;
}

.chat-partner-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #52c41a;
}

.status-dot.online {
  background: #52c41a;
}

.partner-name {
  font-weight: 500;
  color: #262626;
}

.status-text {
  font-size: 12px;
  color: #52c41a;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1890ff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
  margin-right: 8px;
}

/* Load Info Sidebar */
.load-info-sidebar {
  width: 250px;
  border-left: 1px solid #e8e8e8;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
}

.load-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.load-info-header .load-id {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
}

.load-info-details {
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.load-info-details::-webkit-scrollbar {
  width: 6px;
}

.load-info-details::-webkit-scrollbar-thumb {
  background-color: #d9d9d9;
  border-radius: 3px;
}

.load-info-details::-webkit-scrollbar-track {
  background-color: #f5f5f5;
}

.load-info-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.load-info-row .label {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.load-info-row .value {
  font-size: 14px;
  color: #262626;
  font-weight: 500;
}

.load-info-row .value.highlight {
  color: #1890ff;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f9f9f9;
  min-height: 0;
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background-color: #d9d9d9;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-track {
  background-color: transparent;
}

.message {
  display: flex;
  margin-bottom: 12px;
}

.message.own {
  justify-content: flex-end;
}

.message-content {
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.message.own .message-content {
  background: #1890ff;
  color: #fff;
}

.message-sender {
  font-size: 11px;
  color: #1890ff;
  margin-bottom: 4px;
  font-weight: 500;
}

.message-text {
  word-break: break-word;
}

.message-file {
  margin-top: 8px;
}

.file-link {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  font-size: 12px;
  color: inherit;
}

.message.own .file-link {
  background: rgba(255, 255, 255, 0.2);
}

.file-link .anticon {
  margin-right: 4px;
}

.message-time {
  font-size: 10px;
  color: #8c8c8c;
  margin-top: 4px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.message.own .message-time {
  color: rgba(255, 255, 255, 0.7);
}

.read-icon {
  margin-left: 4px;
  font-size: 10px;
}

.no-messages {
  text-align: center;
  color: #8c8c8c;
  padding: 24px;
}

.chat-input {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #e8e8e8;
  gap: 8px;
  flex-shrink: 0;
  background: #fff;
}

.chat-input .ant-input {
  flex: 1;
  border-radius: 20px;
}

.no-chat-selected {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c8c8c;
}

.no-chat-selected p {
  margin-top: 16px;
}
</style>
